// SellerShop.jsx - Shop Profile with Edit Popup and Ratings
import React, { useState, useEffect } from 'react';
import { AiOutlineEdit } from 'react-icons/ai';
import axios from 'axios';
import { toast } from 'react-toastify';

const SellerShop = () => {
  const [shopInfo, setShopInfo] = useState({
    shop_name: '',
    shop_banner_url: '',
    shop_logo_url: '',
    shop_description: '',
    about_shop: ''
  });
  const [showEditPopup, setShowEditPopup] = useState(false);
  const [loading, setLoading] = useState(true);
  const [ratings, setRatings] = useState([]);
  const [ratingsLoading, setRatingsLoading] = useState(true);

  const seller_id = localStorage.getItem('seller_id');

  const IMAGE_BASE_URL = "https://cyberathon.com/multivendor/";


  useEffect(() => {
    if (!seller_id) {
      toast.error('Seller ID not found');
      return;
    }
    fetchShopProfile();
    fetchReviews();
  }, []);

  const fetchShopProfile = async () => {
     const formData = new FormData();
      formData.append('seller_id', seller_id);
    try {
      setLoading(true);
      const response = await axios.post(
        'https://cyberathon.com/multivendor/index.php/Api/Master_controller/get_by_id_shop_profile',
        formData
      );

      if (response.data.status) {
        setShopInfo(response.data.data);
      } else {
        toast.warning(response.data.message || 'No shop profile found');
      }
    } catch (error) {
      console.error('Shop Profile Error:', error);
      toast.error('Failed to fetch shop profile');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      setRatingsLoading(true);
      const response = await axios.post(
        'https://cyberathon.com/multivendor/index.php/Api/Master_controller/get_by_id_review',
        { seller_id }
      );

      if (response.data.status) {
        // Transform API data to match component structure
        const transformedReviews = response.data.data.map(review => ({
          id: review.id,
          reviewer: `User ${review.user_id}`,
          rating: parseInt(review.rating),
          comment: review.your_review,
          product_name: review.product_name,
          image: review.image,
          created_at: review.created_at,
          seller_reply: review.seller_reply || '',
          reply: ''
        }));
        setRatings(transformedReviews);
      } else {
        toast.warning(response.data.message || 'No reviews found');
      }
    } catch (error) {
      console.error('Reviews Error:', error);
      toast.error('Failed to fetch reviews');
    } finally {
      setRatingsLoading(false);
    }
  };

  const handleReplyChange = (id, value) => {
    setRatings(ratings.map(r => r.id === id ? { ...r, reply: value } : r));
  };

  const handleReplySubmit = async (reviewId, replyText) => {
    if (!replyText.trim()) {
      toast.warning('Please write a reply before submitting');
      return;
    }

    try {
      const response = await axios.post(
        'https://cyberathon.com/multivendor/index.php/Api/Master_controller/update_review',
        { 
          id: reviewId,
          seller_reply: replyText 
        }
      );

      if (response.data.status) {
        toast.success(response.data.message || 'Reply submitted successfully');
        // Clear the reply input after successful submission
        setRatings(ratings.map(r => r.id === reviewId ? { ...r, reply: '' } : r));
        // Optionally refresh reviews to get updated data
        fetchReviews();
      } else {
        toast.error(response.data.message || 'Failed to submit reply');
      }
    } catch (error) {
      console.error('Reply Submit Error:', error);
      toast.error('Failed to submit reply');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <>
      <div className="page-content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-12">
              <div className="page-title-box">
                <h4 className="mb-0 fw-semibold">Shop Settings</h4>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item"><a href="#">Dashboard</a></li>
                  <li className="breadcrumb-item active">Shop</li>
                </ol>
              </div>
            </div>
          </div>

          {/* Shop Profile and Ratings */}
          <div className="row mb-4">
            {/* Shop Profile View */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <span>Shop Profile</span>
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={() => setShowEditPopup(true)}
                  >
                    <AiOutlineEdit /> Edit
                  </button>
                </div>
                <div className="card-body">
                  {loading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Shop Name</label>
                        <p className="form-control-plaintext border rounded p-2 bg-light">
                          {shopInfo.shop_name || 'Not provided'}
                        </p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Shop Banner</label>
                        {shopInfo.shop_banner_url ? (
                            <div className="border rounded p-2 bg-light">
                              <img
                                src={`${IMAGE_BASE_URL}${shopInfo.shop_banner_url}`}
                                alt="Shop Banner"
                                className="img-fluid rounded"
                                style={{ maxHeight: "120px", width: "100%", objectFit: "cover" }}
                                onError={(e) => {
                                  e.target.src = "https://via.placeholder.com/600x120?text=Banner+Not+Found";
                                }}
                              />
                            </div>
                          ) : (
                            <p className="form-control-plaintext border rounded p-2 bg-light">
                              Not provided
                            </p>
                          )}
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Shop Logo</label>
                        {shopInfo.shop_logo_url ? (
                          <div className="border rounded p-2 bg-light text-center">
                            <img
                              src={`${IMAGE_BASE_URL}${shopInfo.shop_logo_url}`}
                              alt="Shop Logo"
                              className="img-fluid rounded"
                              style={{ maxHeight: "80px", maxWidth: "80px", objectFit: "contain" }}
                              onError={(e) => {
                                e.target.src = "https://via.placeholder.com/80?text=Logo";
                              }}
                            />
                          </div>
                        ) : (
                          <p className="form-control-plaintext border rounded p-2 bg-light">
                            Not provided
                          </p>
                        )}
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">Shop Description</label>
                        <p className="form-control-plaintext border rounded p-2 bg-light">
                          {shopInfo.shop_description || 'Not provided'}
                        </p>
                      </div>
                      <div className="mb-3">
                        <label className="form-label fw-semibold">About Shop</label>
                        <p className="form-control-plaintext border rounded p-2 bg-light">
                          {shopInfo.about_shop || 'Not provided'}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Shop Ratings */}
            <div className="col-md-6">
              <div className="card">
                <div className="card-header">Shop Ratings & Reviews</div>
                <div className="card-body">
                  {ratingsLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading reviews...</span>
                      </div>
                    </div>
                  ) : ratings.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      <p>No reviews yet</p>
                    </div>
                  ) : (
                    ratings.map((r) => (
                      <div key={r.id} className="border rounded p-3 mb-3">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div>
                            <h6 className="mb-1">
                              {r.reviewer} 
                              <span className="badge bg-info ms-2">{r.rating}★</span>
                            </h6>
                            <small className="text-muted">{formatDate(r.created_at)}</small>
                          </div>
                        </div>
                        
                        {r.product_name && (
                          <p className="mb-1 text-muted">
                            <small><strong>Product:</strong> {r.product_name}</small>
                          </p>
                        )}
                        
                        <p className="mb-2"><strong>Review:</strong> {r.comment}</p>
                        
                        {r.image && r.image.length > 0 && (
                          <div className="mb-2">
                            {r.image.map((img, idx) => (
                              <img 
                                key={idx}
                                src={`https://cyberathon.com/multivendor/${img}`}
                                alt="Review"
                                className="img-thumbnail me-2"
                                style={{ maxHeight: '80px', maxWidth: '80px', objectFit: 'cover' }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ))}
                          </div>
                        )}
                        
                        {r.seller_reply && (
                          <div className="alert alert-success mb-2">
                            <strong>Your Reply:</strong> {r.seller_reply}
                          </div>
                        )}
                        
                        <div className="mb-2">
                          <label className="form-label">Reply</label>
                          <div className="input-group">
                            <input
                              type="text"
                              className="form-control"
                              value={r.reply}
                              onChange={(e) => handleReplyChange(r.id, e.target.value)}
                              placeholder="Write your reply here..."
                            />
                            <button
                              className="btn btn-primary"
                              type="button"
                              onClick={() => handleReplySubmit(r.id, r.reply)}
                            >
                              Send Reply
                            </button>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <footer className="footer mt-4 text-center">
            <div className="container-fluid">© 2025 Seller Portal</div>
          </footer>
        </div>
      </div>

      {/* Edit Shop Profile Popup */}
      <EditShopProfilePopup
        show={showEditPopup}
        onClose={() => setShowEditPopup(false)}
        shopInfo={shopInfo}
        onSuccess={() => {
          setShowEditPopup(false);
          fetchShopProfile();
        }}
      />
    </>
  );
};

/* ================= EDIT SHOP PROFILE POPUP ================= */

const EditShopProfilePopup = ({ show, onClose, shopInfo, onSuccess }) => {
  const seller_id = localStorage.getItem('seller_id');

  const [formData, setFormData] = useState({
  shop_name: '',
  shop_banner: null,
  shop_logo: null,
  shop_description: '',
  about_shop: ''
});

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (show && shopInfo) {
      setFormData({
        shop_name: shopInfo.shop_name || '',
        shop_banner_url: shopInfo.shop_banner_url || '',
        shop_logo_url: shopInfo.shop_logo_url || '',
        shop_description: shopInfo.shop_description || '',
        about_shop: shopInfo.about_shop || ''
      });
    }
  }, [show, shopInfo]);

  const handleInput = (e) => {
  const { name, value } = e.target;
  setFormData(prev => ({ ...prev, [name]: value }));
};

const handleFile = (e) => {
  const { name, files } = e.target;
  setFormData(prev => ({ ...prev, [name]: files[0] }));
};


  const submitForm = async (e) => {
    e.preventDefault();

    if (!seller_id) {
      toast.error('Seller ID not found');
      return;
    }

    try {
      setIsSubmitting(true);

      const response = await axios.post(
        'https://cyberathon.com/multivendor/index.php/Api/Master_controller/update_shop_profile',
        { seller_id, ...formData }
      );

      if (response.data?.status) {
        toast.success(response.data.message || 'Shop profile updated successfully');
        onSuccess();
      } else {
        toast.error(response.data?.message || 'Failed to update shop profile');
      }
    } catch (error) {
      console.error('Update Shop Profile Error:', error);
      toast.error('Something went wrong');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!show) return null;

  return (
    <div className="modal fade show d-block custom-modal-overlay" tabIndex="-1">
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <form onSubmit={submitForm}>
            <div className="modal-header">
              <h5 className="modal-title fw-semibold">Edit Shop Profile</h5>
              <button type="button" className="btn-close" onClick={onClose}></button>
            </div>

            <div className="modal-body">
              <div className="card mb-0">
                <div className="card-header">
                  <h4 className="card-title mb-0">Shop Details</h4>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Shop Name</label>
                        <input
                          type="text"
                          name="shop_name"
                          className="form-control"
                          value={formData.shop_name}
                          onChange={handleInput}
                          placeholder="Enter shop name"
                        />
                      </div>
                    </div>

                    <div className="col-lg-6">
                      <div className="mb-3">
                    <label className="form-label">Shop Banner</label>
                      <input
                        type="file"
                        name="shop_banner"
                        className="form-control"
                        accept="image/*"
                        onChange={handleFile}
                      />
                      {shopInfo.shop_banner_url && (
                        <img
                          src={`https://cyberathon.com/multivendor/${shopInfo.shop_banner_url}`}
                          className="img-fluid mt-2 rounded"
                          style={{ maxHeight: '120px', objectFit: 'cover' }}
                        />
                      )}
                      </div>
                      </div>

                      <div className="col-lg-6">
                      <div className="mb-3">
                       <label className="form-label">Shop Logo</label>
                        <input
                          type="file"
                          name="shop_logo"
                          className="form-control"
                          accept="image/*"
                          onChange={handleFile}
                        />

                        {shopInfo.shop_logo_url && (
                          <img
                            src={`https://cyberathon.com/multivendor/${shopInfo.shop_logo_url}`}
                            className="img-fluid mt-2 rounded"
                            style={{ maxHeight: '80px' }}
                          />
                        )}
                        </div>
                        </div>


                    <div className="col-lg-6">
                      <div className="mb-3">
                        <label className="form-label">Shop Description</label>
                        <textarea
                          name="shop_description"
                          className="form-control"
                          rows="2"
                          value={formData.shop_description}
                          onChange={handleInput}
                          placeholder="Enter shop description"
                        />
                      </div>
                    </div>

                    <div className="col-lg-12">
                      <div className="mb-3">
                        <label className="form-label">About Shop</label>
                        <textarea
                          name="about_shop"
                          className="form-control"
                          rows="3"
                          value={formData.about_shop}
                          onChange={handleInput}
                          placeholder="Tell us about your shop"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer bg-light">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Updating...' : 'Update Shop Profile'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SellerShop;